import jsPDF from 'jspdf';
import { loadImageBase64 } from '../../../utils/loadImageBase64';

const drawBox = (doc, x, y, w, h) => doc.rect(x, y, w, h);

const field = (doc, label, value, x, y) => {
    doc.text(`${label}: ${value || '—'}`, x, y);
};
const drawTableHeader = (doc, text, x, y, w, h = 7) => {
    doc.setFont('helvetica', 'bold');
    doc.rect(x, y, w, h);
    doc.text(text, x + w / 2, y + 5, { align: 'center' });
    doc.setFont('helvetica', 'normal');
};

const RANGE_COLORS = {
    normal: [171, 255, 174],
    mild: [255, 245, 148],
    severe: [251, 159, 174]
};

const drawCell = (doc, label, value, ranges, x, y, w, h = 6) => {
    const classification = classifyValue(value, label, ranges);

    if (classification) {
        const [r, g, b] = RANGE_COLORS[classification];
        doc.setFillColor(r, g, b);
        doc.rect(x, y, w, h, 'F');
    }

    doc.rect(x, y, w, h);

    const text = `${label}: ${value || '—'}`;
    doc.text(text, x + 2, y + 4);
};

const drawResultsTableColumn = (
    doc,
    title,
    fields,
    results,
    ranges,
    x,
    yStart,
    colWidth
) => {
    let y = yStart;

    drawTableHeader(doc, title, x, y, colWidth);
    y += 7;

    fields.forEach((field) => {
        drawCell(doc, field, results[field], ranges, x, y, colWidth);
        y += 6;
    });
};

const sectionTitle = (doc, text, y, h = 8) => {
    const x = 10;
    const w = 190;

    doc.setFillColor(240, 240, 240);
    doc.rect(x, y - 5, w, h, 'F');

    doc.rect(x, y - 5, w, h);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text(text, x + w / 2, y, { align: 'center' });
    doc.setFont('helvetica', 'normal');
};

const classifyValue = (value, field, ranges) => {
    if (!value || !ranges?.[field]) return null;

    const num = Number(value);
    if (isNaN(num)) return null;

    const { normal, mild, severe } = ranges[field];

    if (num >= normal.min && num <= normal.max) return 'normal';
    if (num >= mild.min && num <= mild.max) return 'mild';
    if (num >= severe.min && num <= severe.max) return 'severe';

    return null;
};

const generateEffortPDF = ({
    doc,
    base64,
    desiredWidth,
    calculatedHeight,
    identification,
    generalData,
    clinicalInfo,
    results,
    report,
    ranges
}) => {
    let y = 18;

    doc.addImage(
        base64,
        'JPEG',
        10,
        6,
        desiredWidth,
        calculatedHeight,
        undefined,
        'FAST'
    );

    const titleX = 10;
    const titleY = 6 + calculatedHeight + 6;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.setTextColor(243, 43, 65);
    doc.text('PROVA DE ESFORÇO', titleX, titleY);
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'normal');

    y = titleY + 10;

    sectionTitle(doc, 'IDENTIFICAÇÃO', y);
    drawBox(doc, 10, y + 3, 190, 30);

    field(doc, 'Nome', identification.name, 12, y + 10);
    field(doc, 'Data Nasc.', identification.birthdate, 12, y + 16);
    field(doc, 'Idade', identification.age, 12, y + 22);
    field(doc, 'Sexo', identification.sex, 12, y + 28);

    field(doc, 'Processo', identification.processNumber, 110, y + 10);
    field(doc, 'Peso', identification.weight, 110, y + 16);
    field(doc, 'Altura', identification.height, 110, y + 22);
    field(doc, 'IMC', identification.bmi, 110, y + 28);

    y += 38;

    sectionTitle(doc, 'DADOS GERAIS', y);
    drawBox(doc, 10, y + 3, 190, 18);

    field(doc, 'Proveniência', generalData.origin, 12, y + 10);
    field(doc, 'Médico', generalData.doctor, 110, y + 10);
    field(doc, 'Operadores', generalData.operators, 12, y + 16);

    y += 26;

    sectionTitle(doc, 'INFORMAÇÃO CLÍNICA', y);
    drawBox(doc, 10, y + 3, 190, 18);
    doc.text(`Indicação: ${clinicalInfo}`, 12, y + 12, { maxWidth: 180 });

    y += 26;

    sectionTitle(doc, 'RESULTADOS E COMENTÁRIOS', y);

    const ry = y + 3;

    const pageWidth = 210;
    const margin = 10;
    const usableWidth = pageWidth - margin * 2;

    const gap = 4;

    const cols = 4;

    const colW = (usableWidth - gap * (cols - 1)) / cols;

    const startX = margin;

    const colX = [
        startX,
        startX + colW + gap,
        startX + 2 * (colW + gap),
        startX + 3 * (colW + gap)
    ];

    const classicFields = [
        'Protocolo',
        'Tempo',
        'Suspensão por',
        'PAS repouso',
        'PAS esforço'
    ];
    const chronoFields = [
        'Repouso',
        'Pico FC',
        'FCMPI',
        '% FC prevista',
        'Índice cronotrópico'
    ];
    const ecgFields = ['Extrassístoles SV', 'Extrassístoles V', 'Angina', 'Alt. Seg. ST'];
    const recoverFields = ['METS', '1º min Recup', '3º min Recup', '6º min Recup'];

    drawResultsTableColumn(
        doc,
        'Esforço',
        classicFields,
        results,
        ranges,
        colX[0],
        ry,
        colW
    );
    drawResultsTableColumn(
        doc,
        'Cronotropia',
        chronoFields,
        results,
        ranges,
        colX[1],
        ry,
        colW
    );
    drawResultsTableColumn(doc, 'ECG', ecgFields, results, ranges, colX[2], ry, colW);
    drawResultsTableColumn(
        doc,
        'Recuperação',
        recoverFields,
        results,
        ranges,
        colX[3],
        ry,
        colW
    );

    const maxRows = Math.max(
        classicFields.length,
        chronoFields.length,
        ecgFields.length,
        recoverFields.length
    );

    y += 15 + maxRows * 6;

    sectionTitle(doc, 'RELATÓRIO', y);
    drawBox(doc, 10, y + 3, 190, 60);
    doc.text(report, 12, y + 10, { maxWidth: 184 });

    y += 72;

    doc.text('Técnico: _______________________________', 20, y);
    doc.text('Médico: ________________________________', 120, y);
};

const generateCardioRespiratoryPDF = ({
    doc,
    base64,
    desiredWidth,
    calculatedHeight,
    identification,
    generalData,
    clinicalInfo,
    results,
    report,
    ranges
}) => {
    let y = 18;

    doc.addImage(
        base64,
        'JPEG',
        10,
        6,
        desiredWidth,
        calculatedHeight,
        undefined,
        'FAST'
    );

    const titleX = 10;
    const titleY = 6 + calculatedHeight + 6;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.setTextColor(243, 43, 65);
    doc.text('PROVA DE ESFORÇO CARDIORRESPIRATÓRIA', titleX, titleY);
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'normal');

    y = titleY + 10;

    sectionTitle(doc, 'IDENTIFICAÇÃO', y);
    drawBox(doc, 10, y + 3, 190, 30);

    field(doc, 'Nome', identification.name, 12, y + 10);
    field(doc, 'Processo', identification.processNumber, 12, y + 16);
    field(doc, 'Data Nasc.', identification.birthdate, 12, y + 22);
    field(doc, 'Sexo', identification.sex, 12, y + 28);

    field(doc, 'Peso', identification.weight, 110, y + 10);
    field(doc, 'Altura', identification.height, 110, y + 16);
    field(doc, 'IMC', identification.bmi, 110, y + 22);
    field(doc, 'Idade', identification.age, 110, y + 28);

    y += 38;

    sectionTitle(doc, 'DADOS GERAIS', y);
    drawBox(doc, 10, y + 3, 190, 18);

    field(doc, 'Proveniência', generalData.origin, 12, y + 10);
    field(doc, 'Médico', generalData.doctor, 110, y + 10);
    field(doc, 'Operadores', generalData.operators, 12, y + 16);

    y += 26;

    sectionTitle(doc, 'INFORMAÇÃO CLÍNICA', y);
    drawBox(doc, 10, y + 3, 190, 18);
    doc.text(`Indicação: ${clinicalInfo}`, 12, y + 12, { maxWidth: 180 });

    y += 26;

    sectionTitle(doc, 'RESULTADOS E COMENTÁRIOS', y);

    const ry = y + 3;

    const pageWidth = 210;
    const margin = 10;
    const usableWidth = pageWidth - margin * 2;

    const gap = 4;

    const cols = 4;

    const colW = (usableWidth - gap * (cols - 1)) / cols;

    const startX = margin;

    const colX = [
        startX,
        startX + colW + gap,
        startX + 2 * (colW + gap),
        startX + 3 * (colW + gap)
    ];

    const peClassica = [
        'PA repouso',
        'PA esforço',
        'Extrassístoles V',
        'Angina',
        'Alt. Seg. ST',
        'METS'
    ];
    const perc = [
        'Pico',
        '% previsto',
        'VT1%picoVO2',
        'Quociente Resp.',
        'O2 pulso % prev.'
    ];
    const ventilacao = [
        'FEV1/FVC',
        'Res. Respiratória',
        'Osc. Ventilatória',
        'VE/VCO2',
        'ΔPETCO2'
    ];
    const cronotropia = ['% FC prevista', '1º min recup', '3º min recup'];

    drawResultsTableColumn(
        doc,
        'PE Clássica',
        peClassica,
        results,
        ranges,
        colX[0],
        ry,
        colW
    );
    drawResultsTableColumn(doc, 'PERC', perc, results, ranges, colX[1], ry, colW);
    drawResultsTableColumn(
        doc,
        'Ventilação',
        ventilacao,
        results,
        ranges,
        colX[2],
        ry,
        colW
    );
    drawResultsTableColumn(
        doc,
        'Cronotropia',
        cronotropia,
        results,
        ranges,
        colX[3],
        ry,
        colW
    );

    const maxRows = Math.max(
        peClassica.length,
        perc.length,
        ventilacao.length,
        cronotropia.length
    );

    y += 14 + maxRows * 6;

    sectionTitle(doc, 'RELATÓRIO', y);
    drawBox(doc, 10, y + 2, 190, 80);
    doc.text(report, 12, y + 10, { maxWidth: 184 });

    y += 92;

    doc.text('Técnico: _______________________________', 20, y);
    doc.text('Médico: ________________________________', 120, y);
};

export const generateStressTestPDF = async ({
    testType,
    identification,
    generalData,
    clinicalInfo,
    results,
    report,
    ranges,
    download = true
}) => {
    const doc = new jsPDF({
        orientation: 'p',
        unit: 'mm',
        format: 'a4',
        compress: true
    });

    const { base64, width, height } = await loadImageBase64('/assets/Logo.png');

    const desiredWidth = 35;
    const aspectRatio = height / width;
    const calculatedHeight = desiredWidth * aspectRatio;

    if (testType === 'STRESS_TEST_CP') {
        generateCardioRespiratoryPDF({
            doc,
            base64,
            desiredWidth,
            calculatedHeight,
            identification,
            generalData,
            clinicalInfo,
            results,
            report,
            ranges
        });
    } else {
        generateEffortPDF({
            doc,
            base64,
            desiredWidth,
            calculatedHeight,
            identification,
            generalData,
            clinicalInfo,
            results,
            report,
            ranges
        });
    }

    if (download) {
        doc.save(
            testType === 'STRESS_TEST_CP'
                ? 'prova_esforco_cardiorrespiratoria.pdf'
                : 'prova_esforco.pdf'
        );
        return;
    }

    return doc.output('blob');
};
